import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/adminApi";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";

const inputCls =
  "rounded-none border border-foreground/20 bg-transparent focus-visible:ring-0 focus-visible:border-primary text-sm font-light";

const BLANK_JOURNEY = {
  isNew: true,
  title: "",
  subtitle: "",
  category: "Culture",
  duration: "",
  from: "",
  perPerson: "per person · twin share",
  difficulty: "Easy",
  season: "Year-round",
  groupSize: "Private · 1–8 travellers",
  img: "",
  intro: "",
  highlights: [],
  includes: [],
  excludes: [],
  gallery: [],
  itinerary: [],
};

export default function JourneysTab() {
  const [journeys, setJourneys] = useState([]);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    api
      .get("/content/journeys")
      .then((r) => setJourneys(r.data))
      .catch(() => toast.error("Could not load journeys"));
  }, []);

  const onSaved = (updated, wasNew, oldSlug) => {
    setJourneys((js) =>
      wasNew ? [...js, updated] : js.map((j) => (j.slug === (oldSlug || updated.slug) ? updated : j))
    );
    setSelected(null);
    toast.success(`"${updated.title}" ${wasNew ? "created" : "saved"} — live on the website now.`);
  };

  const onDeleted = (slug) => {
    setJourneys((js) => js.filter((j) => j.slug !== slug));
    setSelected(null);
    toast.success("Journey deleted from the website.");
  };

  if (selected) {
    return (
      <JourneyEditor
        journey={selected}
        onSaved={onSaved}
        onDeleted={onDeleted}
        onCancel={() => setSelected(null)}
      />
    );
  }

  return (
    <div data-testid="journeys-tab">
      <div className="flex items-center justify-between gap-4 mb-8">
        <h2 className="font-serif text-3xl md:text-4xl font-light tracking-tight">
          Journeys <span className="text-foreground/40 text-2xl">({journeys.length})</span>
        </h2>
        <button
          onClick={() => setSelected(BLANK_JOURNEY)}
          data-testid="journey-add-new"
          className="btn-sharp border border-primary bg-primary text-primary-foreground px-5 py-2.5 text-[10px] uppercase tracking-[0.2em] font-medium"
        >
          + New journey
        </button>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        {journeys.map((j) => (
          <button
            key={j.slug}
            onClick={() => setSelected(j)}
            data-testid={`admin-journey-${j.slug}`}
            className="text-left border border-foreground/15 p-5 hover:border-primary transition-colors group"
          >
            <div className="text-[10px] uppercase tracking-[0.24em] text-primary mb-2">
              {j.number} · {j.category}
            </div>
            <div className="font-serif text-2xl group-hover:text-primary transition-colors">{j.title}</div>
            <div className="mt-2 text-xs text-foreground/60">
              {j.duration} · from {j.from} · {j.itinerary?.length || 0} itinerary days
            </div>
            <div className="mt-3 text-[10px] uppercase tracking-[0.22em] text-foreground/50 group-hover:text-primary transition-colors">
              Edit →
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

function JourneyEditor({ journey, onSaved, onDeleted, onCancel }) {
  const isNew = !!journey.isNew;
  const [f, setF] = useState({
    ...journey,
    highlights: (journey.highlights || []).join("\n"),
    includes: (journey.includes || []).join("\n"),
    excludes: (journey.excludes || []).join("\n"),
    gallery: (journey.gallery || []).join("\n"),
    itinerary: journey.itinerary || [],
  });
  const [saving, setSaving] = useState(false);

  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const setDay = (i, k, v) =>
    setF((x) => ({ ...x, itinerary: x.itinerary.map((d, idx) => (idx === i ? { ...d, [k]: v } : d)) }));
  const addDay = () =>
    setF((x) => ({ ...x, itinerary: [...x.itinerary, { day: x.itinerary.length + 1, title: "", detail: "" }] }));
  const removeDay = (i) =>
    setF((x) => ({
      ...x,
      itinerary: x.itinerary.filter((_, idx) => idx !== i).map((d, idx) => ({ ...d, day: idx + 1 })),
    }));

  const save = async () => {
    if (!f.title.trim()) {
      toast.error("Please give the journey a title.");
      return;
    }
    setSaving(true);
    const lines = (s) => s.split("\n").map((l) => l.trim()).filter(Boolean);
    const payload = {
      ...f,
      highlights: lines(f.highlights),
      includes: lines(f.includes),
      excludes: lines(f.excludes),
      gallery: lines(f.gallery),
      itinerary: f.itinerary.map((d, i) => ({ ...d, day: i + 1 })),
    };
    delete payload.isNew;
    try {
      const r = isNew
        ? await api.post("/admin/journeys", payload)
        : await api.put(`/admin/journeys/${journey.slug}`, payload);
      onSaved(r.data, isNew, journey.slug);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save — please try again.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Delete "${journey.title}" from the website? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/journeys/${journey.slug}`);
      onDeleted(journey.slug);
    } catch {
      toast.error("Could not delete — please try again.");
    }
  };

  return (
    <div data-testid="journey-editor">
      <div className="flex items-center justify-between gap-4 mb-8 flex-wrap">
        <h2 className="font-serif text-3xl font-light tracking-tight">
          {isNew ? (
            <>New <em className="italic text-primary">journey</em></>
          ) : (
            <>Editing · <em className="italic text-primary">{journey.title}</em></>
          )}
        </h2>
        <div className="flex gap-3">
          {!isNew && (
            <button onClick={remove} data-testid="journey-editor-delete" className="btn-sharp border border-destructive/50 text-destructive px-5 py-2.5 text-[10px] uppercase tracking-[0.2em]">
              Delete
            </button>
          )}
          <button onClick={onCancel} data-testid="journey-editor-cancel" className="btn-sharp border border-foreground/30 px-5 py-2.5 text-[10px] uppercase tracking-[0.2em]">
            Back
          </button>
          <Button onClick={save} disabled={saving} data-testid="journey-editor-save" className="btn-sharp rounded-none border border-primary bg-primary text-primary-foreground hover:bg-primary px-6 py-2.5 text-[10px] uppercase tracking-[0.2em]">
            {saving ? "Saving…" : isNew ? "Create journey" : "Save changes"}
          </Button>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-5 mb-8">
        <Field label="Title"><Input value={f.title} onChange={set("title")} data-testid="edit-title" className={inputCls} /></Field>
        <Field label="Subtitle"><Input value={f.subtitle} onChange={set("subtitle")} data-testid="edit-subtitle" className={inputCls} /></Field>
        <Field label="Category (Culture / Trek / Festival / Luxury / Wellness)"><Input value={f.category} onChange={set("category")} data-testid="edit-category" className={inputCls} /></Field>
        <Field label="Duration">
          <Input value={f.duration} onChange={set("duration")} data-testid="edit-duration" placeholder='e.g. "7 nights" (lowercase)' className={inputCls} />
        </Field>
        <Field label="Price (From)"><Input value={f.from} onChange={set("from")} data-testid="edit-price" className={inputCls} /></Field>
        <Field label="Price note"><Input value={f.perPerson} onChange={set("perPerson")} className={inputCls} /></Field>
        <Field label="Difficulty"><Input value={f.difficulty} onChange={set("difficulty")} className={inputCls} /></Field>
        <Field label="Season"><Input value={f.season} onChange={set("season")} className={inputCls} /></Field>
        <Field label="Group size"><Input value={f.groupSize} onChange={set("groupSize")} className={inputCls} /></Field>
        <Field label="Hero image">
          <div className="flex gap-2">
            <Input value={f.img} onChange={set("img")} placeholder="Image URL or upload →" className={inputCls} data-testid="edit-img-url" />
            <UploadButton testid="upload-hero" onUploaded={(url) => setF((x) => ({ ...x, img: url }))} />
          </div>
        </Field>
        {!isNew && (
          <Field label="URL slug (/journeys/…) — change carefully, old links break">
            <Input value={f.slug} onChange={set("slug")} data-testid="edit-slug" className={inputCls} />
          </Field>
        )}
      </div>

      <Field label="Intro paragraph">
        <Textarea value={f.intro} onChange={set("intro")} rows={3} data-testid="edit-intro" className={inputCls} />
      </Field>

      <div className="grid md:grid-cols-2 gap-5 mt-6">
        <Field label="Highlights (one per line)"><Textarea value={f.highlights} onChange={set("highlights")} rows={6} className={inputCls} /></Field>
        <Field label="Gallery images (one URL per line)">
          <Textarea value={f.gallery} onChange={set("gallery")} rows={6} className={inputCls} data-testid="edit-gallery" />
          <div className="mt-2">
            <UploadButton
              testid="upload-gallery"
              label="Upload & add to gallery"
              onUploaded={(url) => setF((x) => ({ ...x, gallery: (x.gallery ? x.gallery + "\n" : "") + url }))}
            />
          </div>
        </Field>
        <Field label="Included (one per line)"><Textarea value={f.includes} onChange={set("includes")} rows={7} className={inputCls} /></Field>
        <Field label="Not included (one per line)"><Textarea value={f.excludes} onChange={set("excludes")} rows={7} className={inputCls} /></Field>
      </div>

      <div className="mt-10">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-serif text-2xl font-light">Itinerary — day by day</h3>
          <button onClick={addDay} data-testid="itinerary-add-day" className="btn-sharp border border-foreground/30 px-4 py-2 text-[10px] uppercase tracking-[0.2em]">
            + Add day
          </button>
        </div>
        <div className="space-y-4">
          {f.itinerary.map((d, i) => (
            <div key={i} className="border border-foreground/15 p-4 grid md:grid-cols-[70px_1fr_2fr_auto] gap-3 items-start" data-testid={`itinerary-day-${i + 1}`}>
              <div className="font-serif text-xl text-foreground/50 pt-2">D{i + 1}</div>
              <Input value={d.title} onChange={(e) => setDay(i, "title", e.target.value)} placeholder="Day title" className={inputCls} />
              <Textarea value={d.detail} onChange={(e) => setDay(i, "detail", e.target.value)} placeholder="What happens this day" rows={2} className={inputCls} />
              <button onClick={() => removeDay(i)} aria-label="Remove day" className="text-foreground/40 hover:text-destructive text-xl px-2 pt-1">×</button>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-10 flex justify-end">
        <Button onClick={save} disabled={saving} data-testid="journey-editor-save-bottom" className="btn-sharp rounded-none border border-primary bg-primary text-primary-foreground hover:bg-primary px-8 py-3 text-[10px] uppercase tracking-[0.2em]">
          {saving ? "Saving…" : isNew ? "Create journey" : "Save changes"}
        </Button>
      </div>
    </div>
  );
}

function UploadButton({ onUploaded, testid, label = "Upload photo" }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);

  const onChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Image is too large — max 10 MB.");
      e.target.value = "";
      return;
    }
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/admin/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onUploaded(r.data.url);
      toast.success("Photo uploaded.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed — please try again.");
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };

  return (
    <>
      <input ref={ref} type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={onChange} data-testid={`${testid}-input`} />
      <button
        type="button"
        onClick={() => ref.current?.click()}
        disabled={busy}
        data-testid={testid}
        className="btn-sharp shrink-0 border border-foreground/30 px-4 py-2 text-[10px] uppercase tracking-[0.2em] text-foreground/70 hover:border-primary hover:text-primary transition-colors disabled:opacity-50 whitespace-nowrap"
      >
        {busy ? "Uploading…" : label}
      </button>
    </>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-[0.24em] text-foreground/55 block mb-2">{label}</span>
      {children}
    </label>
  );
}
